import { ActiveAbility } from "../../abilities/ActiveAbility";
import { PassiveAbility } from "../../abilities/PassiveAbility";
import { ReactiveAbility } from "../../abilities/ReactiveAbility";
import { QuantifiedDamage } from "../../categorization/QuantifiedDamage";
import { HandTrigger } from "../../rolls/HandTrigger";
import { RecordStatus } from "../../RecordStatus";
import { SkillName } from "../skills/SkillMap";
import { SkillRequirement } from "../skills/SkillRequirement";
import { ExplicitItemKey } from "./ExplicitItemMap";
import { ModSlot } from "./mods/ModSlot";

export type WeaponAffectingModType = 'Ammo' | 'Spell';
export type ModTypeValue = 'Mod' | 'Body' | WeaponAffectingModType;
export type ItemTypeValue = 'Item' | 'Weapon' | 'Container' | ModTypeValue;
export type ItemCategoryValue = ItemTypeValue | 'Apparel';

export type ItemInitializer = {
    id: string;
    name: string;
    description: string;
    icon: string;
    type: ItemTypeValue;
    explicitType?: ExplicitItemKey;

    cost: number;
    weight: number;

    armor: number;
    armorDc: number;
    armorSuccess: number;
    resilience: number;
    resilienceDc: number;
    resilienceSuccess: number;
    
    handsUsedModifier: number;
    handsAvailableModifier: number;

    skillsUsed: SkillName[];
    skillRequirements: SkillRequirement[];
    handTriggers: HandTrigger[];
    
    actions: ActiveAbility[];
    reactions: ReactiveAbility[];
    passives: PassiveAbility[];

    wornOn: string[];
    slots: ModSlot[];
    blacklistTags: string[];
    tags: string[];

    recordStatus: RecordStatus;
}

export type CrudItemInitializer<TInitializer extends ItemInitializer> = TInitializer & { newTags: string[] };

export type WeaponInitializer = ItemInitializer & {
    type: 'Weapon';
    weaponType: string;
    hands: number;
    range: number;
}

export function isWeaponInitializer(initializer: ItemInitializer): initializer is WeaponInitializer {
    return initializer.type == 'Weapon';
}


export type ModInitializer = ItemInitializer & {
    slotType: string;
    assignedSlotId?: string;
    handsUsedModifier: number;
    handsAvailableModifier: number;
    inheritsHandTriggers?: boolean;
    damageSuite: QuantifiedDamage[];
    customDamageTexts: string[];
    assignableToTags: string[];
}

export type CrudModInitializer<TModInitializer extends ModInitializer> = CrudItemInitializer<TModInitializer> & { newAssignableToTags: string[] };

export function isModInitializer(initializer: ItemInitializer): initializer is ModInitializer {
    return initializer.type == 'Mod'
        || initializer.type == 'Ammo'
        || initializer.type == 'Spell';
}

export function unguardedIsAmmo(item: any): boolean {
    return item?.type == 'Ammo';
}

export type AmmoInitializer = ModInitializer & {
    type: 'Ammo';
    rangeMod?: number;
    rangeOverride?: number;
}

export function isAmmoInitializer(initializer: ItemInitializer): initializer is AmmoInitializer {
    return unguardedIsAmmo(initializer);
}

export type SpellInitializer = ModInitializer & {
    type: 'Spell',
    juice: number;
    requiresAttention: boolean;
}

export function unguardedIsSpell(item: any): boolean {
    return item?.type == 'Spell';
}

export function isSpellInitializer(initializer: ItemInitializer): initializer is SpellInitializer {
    return unguardedIsSpell(initializer);
}




export type UnknownItemInitializer = ItemInitializer
    | WeaponInitializer
    | ModInitializer
    | AmmoInitializer
    | SpellInitializer;